import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t, i18n } = useTranslation();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'cs' : 'en';
    i18n.changeLanguage(newLang);
  };

  return (
    <footer className="bg-[#0f1419] border-t border-[rgba(255,255,255,0.08)] pt-20 pb-10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://public.readdy.ai/ai/img_res/de7a14ac-d629-4bc7-b74b-4536ef7ed887.png" 
                alt="NEXNOVA SOLUTIONS" 
                className="h-8 w-auto"
              />
            </div>
            <h3 className="text-white font-bold text-lg mb-3 whitespace-nowrap">NEXNOVA SOLUTIONS</h3>
            <p className="text-[rgba(255,255,255,0.5)] text-sm italic mb-6">
              {t('footer.tagline')}
            </p>
            <p className="text-[rgba(255,255,255,0.6)] text-sm leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[rgba(255,255,255,0.8)] text-base font-semibold uppercase tracking-wider mb-5 whitespace-nowrap">
              {t('footer.navigation')}
            </h4>
            <nav className="flex flex-col gap-3">
              <button
                onClick={() => scrollToSection('home')}
                className="text-[rgba(255,255,255,0.7)] text-sm hover:text-[#00d4ff] transition-colors duration-300 text-left whitespace-nowrap"
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-[rgba(255,255,255,0.7)] text-sm hover:text-[#00d4ff] transition-colors duration-300 text-left whitespace-nowrap"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-[rgba(255,255,255,0.7)] text-sm hover:text-[#00d4ff] transition-colors duration-300 text-left whitespace-nowrap"
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-[rgba(255,255,255,0.7)] text-sm hover:text-[#00d4ff] transition-colors duration-300 text-left whitespace-nowrap"
              >
                {t('nav.contact')}
              </button>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-[rgba(255,255,255,0.8)] text-base font-semibold uppercase tracking-wider mb-5 whitespace-nowrap">
              {t('footer.contactInfo')}
            </h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-[#00d4ff] text-lg mt-1 w-5 h-5 flex items-center justify-center"></i>
                <div className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed">
                  Kaplanova 2252/8<br />
                  Chodov<br />
                  148 00 Praha 4<br />
                  Czech Republic
                </div>
              </div>
              <a
                href="tel:+420720463376"
                className="flex items-center gap-3 text-[rgba(255,255,255,0.7)] text-sm hover:text-[#00d4ff] transition-colors duration-300 whitespace-nowrap"
              >
                <i className="ri-phone-line text-[#00d4ff] text-lg w-5 h-5 flex items-center justify-center"></i>
                +420 720 463 376
              </a>
              <a
                href="mailto:nexnovasolution@gmail.com"
                className="flex items-center gap-3 text-[rgba(255,255,255,0.7)] text-sm hover:text-[#00d4ff] transition-colors duration-300 break-all"
              >
                <i className="ri-mail-line text-[#00d4ff] text-lg w-5 h-5 flex items-center justify-center"></i>
                nexnovasolution@gmail.com
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[rgba(255,255,255,0.8)] text-base font-semibold uppercase tracking-wider mb-5 whitespace-nowrap">
              {t('footer.servicesTitle')}
            </h4>
            <ul className="flex flex-col gap-2.5">
              <li className="flex items-start gap-2 text-[rgba(255,255,255,0.7)] text-sm">
                <span className="text-[#00d4ff] mt-1.5">•</span>
                <span>{t('services.it.title')}</span>
              </li>
              <li className="flex items-start gap-2 text-[rgba(255,255,255,0.7)] text-sm">
                <span className="text-[#00d4ff] mt-1.5">•</span>
                <span>{t('services.mediation.title')}</span>
              </li>
              <li className="flex items-start gap-2 text-[rgba(255,255,255,0.7)] text-sm">
                <span className="text-[#00d4ff] mt-1.5">•</span>
                <span>{t('services.consulting.title')}</span>
              </li>
              <li className="flex items-start gap-2 text-[rgba(255,255,255,0.7)] text-sm">
                <span className="text-[#00d4ff] mt-1.5">•</span>
                <span>{t('services.technical.title')}</span>
              </li>
              <li className="flex items-start gap-2 text-[rgba(255,255,255,0.7)] text-sm">
                <span className="text-[#00d4ff] mt-1.5">•</span>
                <span>{t('services.marketing.title')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[rgba(255,255,255,0.06)] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[rgba(255,255,255,0.4)] text-xs text-center md:text-left">
            {t('footer.copyright')}
          </p>
          <button
            onClick={toggleLanguage}
            className="px-4 py-2 rounded-full bg-[rgba(255,255,255,0.08)] text-white text-xs font-medium hover:bg-[rgba(255,255,255,0.12)] transition-all duration-300 whitespace-nowrap"
          >
            {i18n.language === 'en' ? 'EN | CZ' : 'CZ | EN'}
          </button>
        </div>
      </div>
    </footer>
  );
}